import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { TagsAPI, ClinicTag } from "../../services/tags.service";

interface ManageTagsPanelProps {
  /** Called after a tag is created or deleted, so the caller can refresh
   *  anything derived from the clinic's tag list (filter pills, etc.). */
  onTagsChanged?: () => void;
}

/** "Manage Tags" dropdown — view every tag this clinic has (even ones with
 *  zero patients), create a new standalone tag, or delete one clinic-wide. */
export const ManageTagsPanel: React.FC<ManageTagsPanelProps> = ({ onTagsChanged }) => {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<ClinicTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    TagsAPI.list()
      .then(setTags)
      .catch(() => toast.error("Could not load tags. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleCreate = async () => {
    const name = newTagName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const tag = await TagsAPI.create(name);
      setNewTagName("");
      // Merge the created tag straight into local state instead of
      // re-fetching the whole list — the create response already has
      // everything a row needs, and re-fetching means a second, much
      // heavier round trip (the list endpoint scans every patient to
      // compute per-tag counts) before the new row would appear.
      setTags((prev) => (prev.some((t) => t.tag_id === tag.tag_id) ? prev : [...prev, tag]));
      onTagsChanged?.();
    } catch {
      toast.error("Could not create tag. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tag: ClinicTag) => {
    const confirmMsg =
      tag.patient_count > 0
        ? `Delete "${tag.name}"? It will be removed from ${tag.patient_count} patient(s).`
        : `Delete "${tag.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    setDeletingId(tag.tag_id);
    try {
      await TagsAPI.delete(tag.tag_id);
      setTags((prev) => prev.filter((t) => t.tag_id !== tag.tag_id));
      onTagsChanged?.();
    } catch {
      toast.error("Could not delete tag. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
      >
        Manage Tags
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-slate-100 p-3">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder="New tag name"
              className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !newTagName.trim()}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}
              aria-label="Create tag"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1v10M1 6h10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <p className="px-3 py-4 text-center text-sm text-slate-400">Loading…</p>
            ) : tags.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-slate-400">No tags yet.</p>
            ) : (
              <ul>
                {tags.map((tag) => (
                  <li
                    key={tag.tag_id}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <span className="truncate text-slate-700">{tag.name}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-slate-400">{tag.patient_count}</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(tag)}
                        disabled={deletingId === tag.tag_id}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                        aria-label={`Delete ${tag.name}`}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z" />
                        </svg>
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTagsPanel;
